/**
 * Driving the 8th Wall engine: load, start, stop.
 *
 * iOS Safari has no WebXR AR, so a live camera overlay there means doing the
 * tracking ourselves. The 8th Wall engine (open-sourced and made free in
 * February 2026) runs its own SLAM over the camera feed in WebAssembly inside
 * stock Safari — no app, no App Clip, the page stays the page. It also runs on
 * Android, from the same code.
 *
 * The engine is fetched from the CDN only when AR is first opened: it is a
 * multi-megabyte WASM bundle that has no business in the normal page load.
 */

import * as THREE from "three";
import type { CameraPipelineModule, HitTestResult, HitTestType, Xr8Api } from "./xr8";
import { createMirrorRoot, placeMirrorOnSurface, syncMirror } from "./arLiveMirror";
import { isWebXrArSupported, startWebXrLiveAr } from "./arLiveWebXr";

// Pinned to the majors the official three.js example ships with. The engine
// loads its SLAM chunk relative to its own URL, which is why it is loaded as a
// script tag rather than bundled: the sibling chunk files have to stay
// reachable next to it.
const ENGINE_SCRIPTS = [
  {
    src: "https://cdn.jsdelivr.net/npm/@8thwall/engine-binary@1/dist/xr.js",
    preloadChunks: "slam",
  },
  { src: "https://cdn.jsdelivr.net/npm/@8thwall/xrextras@1/dist/xrextras.js" },
  { src: "https://cdn.jsdelivr.net/npm/@8thwall/landing-page@1/dist/landing-page.js" },
];

let enginePromise: Promise<void> | null = null;

function loadScript(src: string, preloadChunks?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    if (preloadChunks) {
      script.dataset.preloadChunks = preloadChunks;
    }
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Could not load ${src}`));
    document.head.appendChild(script);
  });
}

/** Fetch the engine once; later calls reuse the same load. */
function loadEngine(): Promise<void> {
  if (!enginePromise) {
    enginePromise = (async () => {
      // The Threejs pipeline module builds its scene from the global THREE,
      // so it must see the same instance the rest of the app uses — two
      // copies of three cannot share objects.
      window.THREE = THREE;
      await Promise.all(
        ENGINE_SCRIPTS.map(({ src, preloadChunks }) => loadScript(src, preloadChunks))
      );
      if (!window.XR8) {
        // xr.js signals readiness with an event; if the script ran before we
        // got here the global is already set and the event has passed.
        await new Promise<void>((resolve) =>
          window.addEventListener("xrloaded", () => resolve(), { once: true })
        );
      }
    })().catch((error) => {
      // A failed load must not poison every later attempt.
      enginePromise = null;
      throw error;
    });
  }
  return enginePromise;
}

/**
 * Ask the engine to track the room at true scale.
 *
 * It defaults to `responsive` scale, which normalises the world so that
 * content keeps a comfortable apparent size whatever the room measures - and
 * therefore renders an assembly modelled in metres at whatever size it likes.
 * Seeing the robot at the size it will actually be built is the point of Live
 * AR, so this has to be `absolute`. The engine throws if it arrives after
 * `XR8.run()`: "Scale can only be changed before calling XR8.run()".
 */
export function configureRealScale(controller: Xr8Api["XrController"]): void {
  controller.configure({ scale: "absolute" });
}

/**
 * What a tap should look for, best first.
 *
 * A plane the engine has actually fitted beats one it is guessing at, which
 * beats a bare feature point - but any of the three is a real place in the
 * room, which is what makes a tap put the robot on the table rather than at
 * arm's length in front of the camera.
 */
export const SURFACE_TYPES: HitTestType[] = ["DETECTED_SURFACE", "ESTIMATED_SURFACE", "FEATURE_POINT"];

/**
 * The hit to stand the robot on: the best kind the engine found, and among
 * those the nearest, which is the surface the finger was actually over.
 */
export function bestSurfaceHit(hits: HitTestResult[]): HitTestResult | null {
  for (const type of SURFACE_TYPES) {
    let best: HitTestResult | null = null;
    for (const hit of hits) {
      if (hit.type === type && (best === null || hit.distance < best.distance)) {
        best = hit;
      }
    }
    if (best) {
      return best;
    }
  }
  return null;
}

export interface LiveArSession {
  stop: () => void;
}

export interface LiveArCallbacks {
  /** The camera feed is on screen; before this the view is a black canvas. */
  onCameraReady?: () => void;
  /** The engine gave up: no camera, no permission, or an unsupported browser. */
  onCameraError?: (error: Error) => void;
  /**
   * A tap landed on the robot rather than on the room.
   *
   * Which part it was, so the pose controls can jump to the joint that
   * moves it — pointing at the elbow beats finding it in a row of chips
   * where every module offers a "joint1".
   */
  onRobotPartTapped?: (part: { instanceId: string | null; linkName: string | null }) => void;
  /**
   * What a tap actually found, for `?ar-debug=1`.
   *
   * Whether tap-to-pose works depends on plumbing only the phone can
   * answer for — did the touch reach us, was there an XR ray, is anything
   * mirrored to hit — and guessing at that from here has been expensive.
   */
  onTapDiagnostics?: (diagnostics: {
    hadPointer: boolean;
    hadXrRay: boolean;
    mirroredParts: number;
    part: { instanceId: string | null; linkName: string | null } | null;
  }) => void;
}

/**
 * The lowest z-index the engine paints its own UI at.
 *
 * It inserts a loading screen, a device-motion permission prompt and an error
 * dialog as children of `document.body`, styled between 800 and 1300.
 */
export const ENGINE_UI_MIN_Z_INDEX = 800;

/**
 * Where the AR overlay sits, which has to be *below* the engine's UI.
 *
 * The overlay is an opaque backdrop over the whole window. Above the engine it
 * hides the permission prompt, and a prompt waiting for a tap nobody can see
 * is a black screen that never becomes AR. Moving the engine's elements into
 * the overlay instead is not an option: it removes them again with
 * `document.body.removeChild`, which throws once they are somebody else's
 * children, leaving the prompt on screen and the flow dead.
 *
 * Nothing in the app paints above this except the scene's right-click menu,
 * which cannot be open on a phone.
 */
export const AR_OVERLAY_Z_INDEX = 700;

/**
 * Where the camera canvas sits: under the overlay's controls, over the page.
 *
 * The engine's FullWindowCanvas module moves the canvas out of the overlay and
 * appends it to `document.body`, sizing it 100% x 100% but leaving it
 * statically positioned - so every positioned element on the page, the
 * overlay's own backdrop first among them, paints on top of the camera feed.
 * That is what a black Live AR screen actually was. The module only ever
 * assigns width, height, margin, padding, border and display, so position and
 * z-index are ours to set and stay set across its resize handling.
 */
export const AR_CANVAS_Z_INDEX = AR_OVERLAY_Z_INDEX - 1;

/**
 * Put the camera canvas in front of the page, wherever the engine has moved it.
 */
export function placeCameraCanvas(canvas: HTMLCanvasElement): void {
  canvas.style.position = "fixed";
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.zIndex = String(AR_CANVAS_Z_INDEX);
}

/**
 * Start the camera and mirror the given scene's robot into the room.
 *
 * Resolving means the pipeline is attached and can be stopped again, not that
 * the camera is live -- that comes later, through `onCameraReady`, because the
 * engine has a permission prompt to get through first. Anything that stops it
 * getting there arrives as `onCameraError` rather than as a black screen.
 *
 * The returned session's `stop` shuts the camera down and detaches the
 * pipeline, after which the builder is exactly as it was.
 */
export async function startLiveAr(
  canvas: HTMLCanvasElement,
  getSourceScene: () => THREE.Object3D | null,
  callbacks: LiveArCallbacks = {},
  overlayRoot: HTMLElement | null = null
): Promise<LiveArSession> {
  // Where the browser has ARCore-backed WebXR — Android Chrome — use it: real
  // plane detection and OS-grade tracking, and no engine download at all.
  // Decided before loading anything, so the WebXR path never pays for the
  // 8th Wall bundle.
  if (await isWebXrArSupported()) {
    return startWebXrLiveAr(canvas, getSourceScene, callbacks, overlayRoot);
  }

  await loadEngine();
  const XR8 = window.XR8;
  const XRExtras = window.XRExtras;
  const LandingPage = window.LandingPage;
  if (!XR8 || !XRExtras || !LandingPage) {
    throw new Error("The AR engine did not initialise.");
  }

  const mirrorRoot = createMirrorRoot();
  const clones = new Map<string, THREE.Mesh>();
  // The engine reports the same failure through both hooks in some cases, and
  // a status can arrive after the view is already up; only the first outcome
  // is news.
  let settled = false;
  const cameraReady = () => {
    if (!settled) {
      settled = true;
      callbacks.onCameraReady?.();
    }
  };
  const cameraFailed = (error: Error) => {
    if (!settled) {
      settled = true;
      callbacks.onCameraError?.(error);
    }
  };

  const mirrorModule: CameraPipelineModule = {
    name: "robot-mirror",
    onStart: () => {
      // FullWindowCanvas has reparented the canvas by now.
      placeCameraCanvas(canvas);
      const { scene, camera } = XR8.Threejs.xrScene();

      scene.add(mirrorRoot);
      scene.add(new THREE.AmbientLight(0xffffff, 0.9));
      const sun = new THREE.DirectionalLight(0xffffff, 1.2);
      sun.position.set(3, 10, 5);
      scene.add(sun);

      // Standing eye height, a couple of steps back from the robot. SLAM
      // takes over from here; this only decides where the content starts
      // relative to the first camera pose.
      camera.position.set(0, 1.6, 3);
      XR8.XrController.updateCameraProjectionMatrix({
        origin: camera.position,
        facing: camera.quaternion,
      });
    },
    onUpdate: () => {
      const source = getSourceScene();
      if (source) {
        syncMirror(source, mirrorRoot, clones);
      }
    },
    onCameraStatusChange: ({ status }) => {
      if (status === "hasVideo") {
        // The module restyles the canvas as the video size settles; keep it in
        // front each time rather than trusting one pass at startup.
        placeCameraCanvas(canvas);
        cameraReady();
      } else if (status === "failed") {
        cameraFailed(
          new Error("The camera could not be started. Check that this page may use it.")
        );
      }
    },
    onException: (error) => {
      cameraFailed(error instanceof Error ? error : new Error(String(error)));
    },
  };

  // A single tap stands the robot on whatever the engine recognises under the
  // finger. Quick Look gets that from ARKit's plane detection; here it is a
  // hit test against the engine's own reconstruction, which is as close as
  // this side of the platform gets to putting the robot on that table.
  const handlePlace = (event: TouchEvent) => {
    if (event.touches.length !== 1) {
      return;
    }
    const touch = event.touches[0];
    const hit = bestSurfaceHit(
      XR8.XrController.hitTest(
        touch.clientX / window.innerWidth,
        touch.clientY / window.innerHeight,
        SURFACE_TYPES
      )
    );
    if (hit) {
      // Feature points have a position but no trustworthy orientation, so
      // they keep the robot level; real surfaces orient it - floor, wall or
      // ramp alike, it is the same coordinate transform.
      placeMirrorOnSurface(
        mirrorRoot,
        hit.position,
        hit.type === "FEATURE_POINT" ? undefined : hit.rotation
      );
      return;
    }
    // Nothing recognised there yet: fall back to the gesture the engine always
    // understands, rather than leaving the tap doing nothing at all.
    XR8.XrController.recenter();
  };
  canvas.addEventListener("touchstart", handlePlace, true);
  // The browser must not claim the touches for scrolling.
  const handleTouchMove = (event: TouchEvent) => event.preventDefault();
  canvas.addEventListener("touchmove", handleTouchMove);

  XR8.clearCameraPipelineModules();
  XR8.addCameraPipelineModules([
    XR8.GlTextureRenderer.pipelineModule(),
    XR8.Threejs.pipelineModule(),
    XR8.XrController.pipelineModule(),
    LandingPage.pipelineModule(),
    XRExtras.FullWindowCanvas.pipelineModule(),
    XRExtras.Loading.pipelineModule(),
    XRExtras.RuntimeError.pipelineModule(),
    mirrorModule,
  ]);
  // Before run(), or the engine refuses it.
  configureRealScale(XR8.XrController);
  XR8.run({ canvas });

  return {
    stop: () => {
      canvas.removeEventListener("touchstart", handlePlace, true);
      canvas.removeEventListener("touchmove", handleTouchMove);
      XR8.stop();
      XR8.clearCameraPipelineModules();
      clones.clear();
      // React only unmounts what is still inside the overlay, and the canvas
      // is a child of the body by now, so closing AR would otherwise leave it
      // over the builder.
      canvas.remove();
    },
  };
}
